import React, { useEffect, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useNavigate } from 'react-router';
import { format, endOfDay } from 'date-fns';
import buildUrl from '../../buildUrl';
import useSearch from '../../hooks/useSearch';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import DatePicker from '../../DatePicker';
import { lighten } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InputLabel from '@mui/material/InputLabel';
import ConfirmRevertToDraftDialog from '../dialogs/ConfirmRevertToDraftDialog';
import ConfirmDeleteDialog from '../dialogs/ConfirmDeleteDialog';
import RenameDialog from '../dialogs/RenameDialog';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TopicFilter from '../../topics/TopicFilter';
import Picker from '../../picker/Picker';
import SendIcon from '@mui/icons-material/Send';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import EmailIcon from '@mui/icons-material/Email';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import MailLockIcon from '@mui/icons-material/MailLock';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import useApi from '../../hooks/useApi';
import useEditorHubConnection from '../hooks/useEditorHubConnection';
import useSnackbar from '../../hooks/useSnackbar';
import DashboardInUseDialog from '../dialogs/DashboardInUseDialog';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CopyToAccountsDialog from '../dialogs/CopyToAccountsDialog';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';

const actionsCell = { width: '7%' };

const iconStyle = {
    marginRight: 2,
    verticalAlign: 'middle'
};

const ToolbarItem = ({ children, marginRight = 0 }) => <Box sx={{ display: 'inline-flex', margin: 0.5, marginRight }}>{children}</Box>;

const EmptyTable = ({ isLoading, filtersApplied }) => {
    if (isLoading) {
        return null;
    }

    return (
        <Box sx={{ padding: 6, textAlign: 'center' }}>
            {filtersApplied ? (
                <Typography>No data</Typography>
            ) : (
                <>
                    <Typography>You haven't created any Emails yet.</Typography>
                    <Typography>Click "New Email" to create your first one!</Typography>
                </>
            )}
        </Box>
    );
};

const EmailNameStatus = ({ name, status, locked }) => {
    const StatusIcon = () => {
        if (status === 'Sent') {
            return <MarkEmailReadIcon fontSize="small" color="success" sx={iconStyle} />;
        }

        if (status === 'Sending') {
            return <SendIcon fontSize="small" color="info" sx={iconStyle} />;
        }

        if (status === 'Scheduled') {
            return <ScheduleSendIcon fontSize="small" color="info" sx={iconStyle} />;
        }

        return <EmailIcon fontSize="small" color="disabled" sx={iconStyle} />;
    };

    if (locked) {
        status += ' (locked)';
    }

    // Inner fragment used below to get around this issue: https://github.com/mui/material-ui/issues/31261

    return (
        <>
            <Tooltip title={status} arrow>
                {locked ? <MailLockIcon fontSize="small" color="warning" sx={iconStyle} /> : (
                    <>
                        <StatusIcon />
                    </>
                )}
            </Tooltip>
            {name}
        </>
    );
};

const EmailMenu = ({
    onDelete,
    onRename,
    onCopy,
    status,
    onEdit,
    onViewReport,
    onCancelAndRevertToDraft,
    userAccounts,
    onCopyToAccounts,
    isBeingUsed = false
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();

        setAnchorEl(e.currentTarget);
    };

    const handleClose = e => {
        e.preventDefault();
        e.stopPropagation();

        setAnchorEl(null);
    };

    const handleAction = (e, action) => {
        e.stopPropagation();

        action();
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton onClick={handleClick} size="small" disabled={isBeingUsed}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {onEdit && (
                    <MenuItem onClick={e => handleAction(e, onEdit)}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit Draft</ListItemText>
                    </MenuItem>
                )}
                {onViewReport && (
                    <MenuItem onClick={e => handleAction(e, onViewReport)}>
                        <ListItemIcon>
                            <BarChartIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View Report</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={e => handleAction(e, onRename)}>
                    <ListItemIcon>
                        <DriveFileRenameOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem onClick={e => handleAction(e, onCopy)}>
                    <ListItemIcon>
                        <ContentCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Create Copy</ListItemText>
                </MenuItem>
                {userAccounts.length > 1 && (
                    <MenuItem onClick={e => handleAction(e, onCopyToAccounts)}>
                        <ListItemIcon>
                            <CopyAllIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Copy to Account(s)</ListItemText>
                    </MenuItem>
                )}
                {status === 'Scheduled' ? (
                    <Box>
                        <MenuItem onClick={e => handleAction(e, onCancelAndRevertToDraft)}>
                            <ListItemIcon>
                                <CancelScheduleSendIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Cancel & Revert to Draft</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={e => handleAction(e, onDelete)}>
                            <ListItemIcon>
                                <CancelScheduleSendIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Cancel & Delete Campaign</ListItemText>
                        </MenuItem>
                    </Box>
                ) : (
                    <MenuItem onClick={e => handleAction(e, onDelete)}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

const CreateNewMenu = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCreateNew = method => {
        navigate(`/editor/email/${method}`);
        handleClose();
    };

    return (
        <>
            <Button
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                sx={{ mr: 1, whiteSpace: 'pre' }}
            >
                New Email
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={() => handleCreateNew('new')}>From Template</MenuItem>
                <MenuItem onClick={() => handleCreateNew('upload')}>Upload HTML</MenuItem>
            </Menu>
        </>
    );
};

const rowsPerPageOptions = [10, 25, 50];

const Emails = () => {
    const { data: user } = useSuspenseFetch('user');
    const { data: topics } = useSuspenseFetch('topics');
    const navigate = useNavigate();
    const [isInitialising, setIsInitialising] = useState(true);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [page, setPage] = useState(0);
    const [orderBy, setOrderBy] = useState('Modified');
    const [orderDesc, setOrderDesc] = useState(true);
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue);
    const [isLoading, setIsLoading] = useState(true);
    const [itemToRename, setItemToRename] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [itemToRevertToDraft, setItemToRevertToDraft] = useState(null);
    const [itemToCopy, setItemToCopy] = useState(null);
    const [isCopying, setIsCopying] = useState(false);
    const [status, setStatus] = useState('');
    const [filterTopics, setFilterTopics] = useState([]);
    const { handleGet, handlePut, handlePost, handleDelete } = useApi();
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const { connection, usersEditing, setUsersEditing } = useEditorHubConnection();
    const [showInUseDialog, setShowInUseDialog] = useState(false);
    const [itemToCopyToAccounts, setItemToCopyToAccounts] = useState(null);

    const handleFetchCount = async () => {
        const params = {
            search: searchValue,
            ...(fromDate && { fromDate: format(fromDate, 'yyyy-MM-dd') }),
            ...(toDate && { toDate: format(endOfDay(toDate), 'yyyy-MM-dd HH:mm:ss') }),
            ...(status !== '' && { status }),
            ...(filterTopics.length > 0 && { topicIds: filterTopics.map(e => e.id) })
        };

        const url = buildUrl('emails/count', params);
        const response = await handleGet(url);

        if (!response.ok) {
            showSnackbar('Error fetching count', 'error');
            return;
        }

        const data = await response.json();

        setCount(data);
    };

    const handleFetchItems = async () => {
        const params = {
            search: searchValue,
            ...(fromDate && { fromDate: format(fromDate, 'yyyy-MM-dd') }),
            ...(toDate && { toDate: format(endOfDay(toDate), 'yyyy-MM-dd HH:mm:ss') }),
            skip: page * rowsPerPage,
            take: rowsPerPage,
            orderBy,
            isDescending: orderDesc,
            ...(status !== '' && { status }),
            ...(filterTopics.length > 0 && { topicIds: filterTopics.map(e => e.id) })
        };

        const url = buildUrl('emails', params);
        const response = await handleGet(url);

        if (!response.ok) {
            showSnackbar('Error fetching emails', 'error');
            setIsLoading(false);
            return;
        }

        const data = await response.json();

        setItems(data);
        setIsLoading(false);
    };

    const handleSort = (field, defaultOrder) => {
        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(defaultOrder);
        }
    };

    const handleUpdateItem = async ({ item, topics = null, name = null }) => {
        const topicIds = topics?.map(e => e.id);

        const params = {
            autoSave: false,
            ...(name && { name }),
            ...(topicIds && { topicIds })
        };

        const response = await handlePut(`emails/${item.id}`, params);

        if (!response.ok) {
            showSnackbar('Email not found', 'error');
        }

        setItemToRename(null);
        handleFetchItems();
    };

    const handleCopy = async name => {
        setIsCopying(true);

        const response = await handlePost('emails', {
            emailId: itemToCopy.id,
            name
        });

        if (response.ok) {
            handleFetchItems();
            handleFetchCount();
        }
        else {
            showSnackbar('Email could not be copied', 'error');
        }

        setIsCopying(false);
        setItemToCopy(null);
    };

    const handleCopyToAccounts = async (name, userAccounts) => {
        setIsCopying(true);

        const response = await handlePost('emails', {
            emailId: itemToCopyToAccounts.id,
            name,
            userAccounts
        });

        if (response.ok) {
            const message = userAccounts.length === 1
                ? 'Email copied successfully'
                : 'Email successfully copied to accounts';

            showSnackbar(message, 'success');
            handleFetchItems();
            handleFetchCount();
        }

        setIsCopying(false);
        setItemToCopyToAccounts(null);
    };

    const handleDeleteItem = async id => {
        const response = await handleDelete(`emails/${id}`);

        if (!response.ok) {
            showSnackbar('Email not found', 'error');
        }

        setItemToDelete(null);
        handleFetchCount();
        handleFetchItems();
    };

    const handleRevertToDraft = async id => {
        const emailResponse = await handleDelete(`campaigns/${id}`);

        if (!emailResponse.ok) {
            showSnackbar('Email not found', 'error');
        }

        setItemToRevertToDraft(null);
        handleFetchItems();
    };

    const handleViewReport = campaignId => {
        navigate(`/reports/email/${campaignId}`);
    };

    const handleEditItem = id => {
        navigate(`/editor/email/${id}`);
    };

    const handleClickRow = item => {
        if (item.campaignId) {
            handleViewReport(item.campaignId);
        }
        else {
            handleEditItem(item.id);
        }
    };

    const handleChangeTopicsFilter = topic => {
        if (!topic) {
            setFilterTopics([]);
            return;
        }

        if (Boolean(filterTopics.find(e => e.id === topic.id))) {
            setFilterTopics(filterTopics.filter(e => e.id !== topic.id));
        }
        else {
            setFilterTopics([...filterTopics, topic]);
        }
    };

    const handleInit = async () => {
        await handleFetchItems();
        await handleFetchCount();
        setIsInitialising(false);
    };

    const getUsersEditing = async () => {
        try {
            await connection.start().then(() => {
                connection.invoke('GetEditorsInUse');
            });

            connection.on('GetEditorsInUse', usersEditing => {
                setUsersEditing(usersEditing);
            });
        }
        catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (isInitialising) {
            return;
        }

        handleFetchCount();
    }, [searchValue, fromDate, toDate, status, filterTopics]);

    useEffect(() => {
        setPage(0);
    }, [searchValue, fromDate, toDate, orderBy, orderDesc, rowsPerPage, status, filterTopics]);

    useEffect(() => {
        setIsLoading(true);
    }, [searchValue, fromDate, toDate, orderBy, orderDesc, rowsPerPage, page, status, filterTopics]);

    useEffect(() => {
        if (isInitialising) {
            return;
        }

        isLoading && handleFetchItems();
    }, [isLoading]);

    useEffect(() => {
        handleInit();
    }, []);

    useEffect(() => {
        if (connection && connection.state !== HubConnectionState.Connected) {
            getUsersEditing();
        }
    }, [connection]);

    if (isInitialising) {
        return <LoadingOverlay />;
    }

    return (
        <>
            <Toolbar disableGutters sx={{ mx: 1, paddingTop: 0.5, paddingBottom: 0.5 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <ToolbarItem>
                        <OutlinedInput
                            value={tempSearchValue}
                            onChange={e => setTempSearchValue(e.target.value)}
                            placeholder="Search..."
                            size="small"
                            sx={{ mr: 1 }}
                            endAdornment={(
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setTempSearchValue('')}
                                        edge="end"
                                        disabled={!Boolean(tempSearchValue)}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )}
                        />
                    </ToolbarItem>
                    <ToolbarItem>
                        <TopicFilter
                            availableTopics={topics}
                            selectedTopics={filterTopics}
                            onAddTopic={topic => handleChangeTopicsFilter(topic)}
                            onRemoveTopic={topic => handleChangeTopicsFilter(topic)}
                            onClearTopics={() => handleChangeTopicsFilter()}
                        />
                    </ToolbarItem>
                    <ToolbarItem marginRight={1}>
                        <DatePicker value={fromDate} onChange={setFromDate} label="Modified from date" />
                    </ToolbarItem>
                    <ToolbarItem marginRight={1}>
                        <DatePicker value={toDate} onChange={setToDate} label="Modified to date" />
                    </ToolbarItem>
                    <ToolbarItem>
                        <FormControl margin="none" size="small">
                            <InputLabel id="filter-status-label">Filter Status</InputLabel>
                            <Select
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                sx={{ minWidth: 160 }}
                                labelId="filter-status-label"
                                label="Filter Status"
                            >
                                <MenuItem value={''}>All</MenuItem>
                                <MenuItem value={'Sent'}>
                                    <MarkEmailReadIcon fontSize="small" color="success" sx={iconStyle} />
                                    Sent
                                </MenuItem>
                                <MenuItem value={'Sending'}>
                                    <SendIcon fontSize="small" color="info" sx={iconStyle} />
                                    Sending
                                </MenuItem>
                                <MenuItem value={'Scheduled'}>
                                    <ScheduleSendIcon fontSize="small" color="info" sx={iconStyle} />
                                    Scheduled
                                </MenuItem>
                                <MenuItem value={'Draft'}>
                                    <EmailIcon fontSize="small" color="disabled" sx={iconStyle} />
                                    Draft
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </ToolbarItem>
                </Box>
                <ToolbarItem>
                    <CreateNewMenu />
                </ToolbarItem>
            </Toolbar>
            <Divider />
            {items.length > 0 ? (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '30%' }}>
                                        <TableSortLabel
                                            active={orderBy === 'Name'}
                                            direction={orderDesc ? 'desc' : 'asc'}
                                            onClick={() => handleSort('Name', false)}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '20%' }}>Send Time</TableCell>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '20%' }}>
                                        Topics
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '10%' }}>
                                        Activity
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="right" sx={{ width: '20%' }}>
                                        <TableSortLabel
                                            active={orderBy === 'Modified'}
                                            direction={orderDesc ? 'desc' : 'asc'}
                                            onClick={() => handleSort('Modified', false)}
                                        >
                                            Last Modified
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(e => {
                                    const beingUsedBy = usersEditing.filter(editor => editor.emailId === e.id);
                                    const isBeingUsed = !!beingUsedBy.length;

                                    return (
                                        <TableRow
                                            key={e.id}
                                            hover={!isBeingUsed}
                                            sx={
                                                isBeingUsed
                                                    ? { bgcolor: lighten(theme.palette.warning.light, 0.7) }
                                                    : {}
                                            }
                                        >
                                            <TableCell
                                                align="left"
                                                onClick={() =>
                                                    !isBeingUsed ? handleClickRow(e) : setShowInUseDialog(true)
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {isLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : (
                                                    <EmailNameStatus
                                                        name={e.name}
                                                        status={e.status}
                                                        locked={isBeingUsed}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="left" sx={{ cursor: 'default' }}>
                                                {e.sendTime && e.status === 'Scheduled' ? (
                                                    <>
                                                        <AccessTimeIcon color="primary" size="small" sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                                                        {format(new Date(e.sendTime), 'dd/MM/yyyy HH:mm')}
                                                    </>
                                                ) : (
                                                    e.sendTime ? format(new Date(e.sendTime), 'dd/MM/yyyy HH:mm') : '-'
                                                )}
                                            </TableCell>
                                            <TableCell align="left" sx={{ p: 0 }}>
                                                <Picker
                                                    availableItems={topics}
                                                    selectedItems={e.topics}
                                                    onChangeItems={topics => handleUpdateItem({ item: e, topics })}
                                                    onClickItem={topic => handleChangeTopicsFilter(topic)}
                                                    isBeingUsed={isBeingUsed}
                                                />
                                            </TableCell>
                                            <TableCell align="left" sx={{ cursor: 'default' }}>
                                                {beingUsedBy.map(user => (
                                                    <Tooltip
                                                        key={user.email}
                                                        title={`${user.name || user.email} is currently editing`}
                                                    >
                                                        <Avatar src={user.avatarURL} sx={{ width: 30, height: 30 }} />
                                                    </Tooltip>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right" sx={{ cursor: 'default' }}>
                                                {e.modified ? format(new Date(e.modified), 'dd/MM/yyyy HH:mm') : '-'}
                                            </TableCell>
                                            <TableCell align="center" sx={actionsCell}>
                                                <EmailMenu
                                                    status={e.status}
                                                    onDelete={() => setItemToDelete(e)}
                                                    onRename={() => setItemToRename(e)}
                                                    onCopy={() => setItemToCopy(e)}
                                                    onEdit={e.campaignId ? null : () => handleEditItem(e.id)}
                                                    onViewReport={
                                                        e.campaignId ? () => handleViewReport(e.campaignId) : null
                                                    }
                                                    onCancelAndRevertToDraft={() => setItemToRevertToDraft(e)}
                                                    isBeingUsed={isBeingUsed}
                                                    userAccounts={user.accounts}
                                                    onCopyToAccounts={() => setItemToCopyToAccounts(e)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                    />
                </>
            ) : (
                <EmptyTable
                    isLoading={isLoading}
                    filtersApplied={searchValue || fromDate || toDate || status || filterTopics.length > 0}
                />
            )}
            {isLoading && <LoadingOverlay />}

            {itemToDelete && (
                <ConfirmDeleteDialog
                    onClose={() => setItemToDelete(null)}
                    onDelete={handleDeleteItem}
                    item={itemToDelete}
                    type="email"
                />
            )}
            {itemToRevertToDraft && (
                <ConfirmRevertToDraftDialog
                    onClose={() => setItemToRevertToDraft(null)}
                    onRevertToDraft={handleRevertToDraft}
                    item={itemToRevertToDraft}
                    type="email"
                />
            )}
            {itemToRename && (
                <RenameDialog
                    onClose={() => setItemToRename(null)}
                    onSubmit={name => handleUpdateItem({ item: itemToRename, name })}
                    itemName={itemToRename.name}
                    type="email"
                />
            )}
            {itemToCopy && (
                <RenameDialog
                    onClose={() => setItemToCopy(null)}
                    onSubmit={handleCopy}
                    itemName={itemToCopy.name}
                    isCopy={true}
                    isCopying={isCopying}
                    type="email"
                />
            )}
            {itemToCopyToAccounts && (
                <CopyToAccountsDialog
                    onClose={() => setItemToCopyToAccounts(null)}
                    onSubmit={handleCopyToAccounts}
                    itemName={itemToCopyToAccounts.name}
                    isCopying={isCopying}
                    type="email"
                />
            )}
            {showInUseDialog && <DashboardInUseDialog onClose={() => setShowInUseDialog(false)} type="email" />}
        </>
    );
};

export default Emails;
